import {GruppeMedlemListeModell, MøteDato, MøteModell, MøteModellEnkel, MøteModellEnkelMedProtokollinfo, TilgangsrolleMedlem, TynnSakModell} from "../tjenester/ressurser/generertNg2HttpKlient";
import {antallDagerIPeriode, lesDatoPeriode, periodeErPassert, skrivDatoPeriode, ZonedDateTimePeriode} from "../støttefunksjoner/zonedDateTimePerioder";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";

export type MøteMedDato = Pick<MøteModell, 'dato'>;

/** @deprecated, bruke lesMøtedato2 */
export function lesMøtedato(møte: MøteMedDato, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriode {
    return lesDatoPeriode(møte.dato, tidssone);
}

export function lesMøtedato2(møte: MøteMedDato, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket {
    return ZonedDateTimePeriodeLukket.les(møte.dato, tidssone);
}

/** @deprecated, bruke ZonedDateTimePeriodeLukket.somSerialiserbar() */
export function skrivMøtedato(periode: ZonedDateTimePeriode): MøteDato {
    return skrivDatoPeriode(periode) as MøteDato;
}

export function erMøteAvholdt(møte: MøteMedDato, klokke: Klokke): boolean {
    return periodeErPassert(lesMøtedato(møte), klokke);
}

export function erFlerdagsmøte(møte: MøteMedDato) {
    return møtevarighetIAntallDager(møte.dato) > 1;
}

export function møtevarighetIAntallDager(møtedato: MøteDato) {
    return antallDagerIPeriode(lesDatoPeriode(møtedato));
}

/** Sortere på møte.dato.start, eldste først */
export function sorterMøterPåDato<T extends MøteMedDato>(a: T, b: T): number {
    if (a === b) return 0;
    return lesMøtedato2(a).sammenliknMed(lesMøtedato2(b));
}

/**
 * Returnerer subset av angitt tilgjengeligMedlemmer liste
 * bestående av medlemmer som er registret som deltakere på angitt møte.
 *
 * Merk at dette settet vil avvike fra møte.deltakere ved at feks deaktiverte/anonymiserte
 * medlemmer som normal ikke vil være inkludert i tilgjengeligMedlemmer men som er med i
 * møte.deltakere ikke vil være med i returnert liste.
 *
 * @Deprecate - hent møtedeltakere fra møtedeltakereDatatjeneste, bruk tilsvarende funksjon i møtedeltakerModellHjelpere.ts
 */
export function medlemmerSomDeltarPåMøte(møte: MøteModell, tilgjengeligMedlemmer: GruppeMedlemListeModell[]): GruppeMedlemListeModell[] {
    const møteMedlemmer = new Set(møte.deltakere.map(deltaker => deltaker.medlem.id));
    return tilgjengeligMedlemmer.filter(medlem => møteMedlemmer.has(medlem.id));
}

/** Returnerer saker fra {@link MøteModellEnkelMedProtokollinfo#protokollBehandlesI} som er knyttet til møter som er
 * tidligere eller lik {@link MøteModellEnkelMedProtokollinfo#møte}.
 * Altså der sak.møte.dato <= møte.dato.
 * Vi returnerer altså de protokolbehandlinger som anses å være 'ikke korrekte' i den forstand at protokoll normalt
 * skal behandles i et etterfølgende møte.  */
export function forTidligeProtokollbehandlinger(møteinfo: MøteModellEnkelMedProtokollinfo): TynnSakModell[] {
    return møteinfo.protokollBehandlesI
      .filter(behandling => sammenliknMøteMedProtokollbehandling(møteinfo, behandling) >= 0);
}


/** Returnerer saker fra {@link MøteModellEnkelMedProtokollinfo#protokollBehandlesI} som er knyttet til møter som er
 * senere enn {@link MøteModellEnkelMedProtokollinfo#møte}.
 * Altså der sak.møte.dato > møte.dato. */
export function påfølgendeProtokollbehandlinger(møteinfo: MøteModellEnkelMedProtokollinfo): TynnSakModell[] {
    return møteinfo.protokollBehandlesI
      .filter(behandling => sammenliknMøteMedProtokollbehandling(møteinfo, behandling) < 0);
}

/** Sammenlikner møte i møteinfo med angitt behandling, returnerer negativt tall dersom møteinfo.møte er tidligere enn behandling.møte,
 * 0 dersom det er samme møte, positivt tall dersom møteinfo.møte er senere enn behandling.møte.
 * Dersom møtedatoene er like sammenliknes nummerIPlan */
function sammenliknMøteMedProtokollbehandling(møteinfo: MøteModellEnkelMedProtokollinfo, behandling: TynnSakModell): number {
    const sortertPåDato = lesMøtedato2(møteinfo.moete).sammenliknMed(lesMøtedato2(behandling.moete));
    if (sortertPåDato != 0) return sortertPåDato;
    return møteinfo.moete.nummerIPlan - behandling.moete.nummerIPlan;
}

/** Sann dersom møtet ikke redigerbart.
 * Enten fordi møtet er låst for redigering (protokoll sendt til signering) eller forbi bruker ikke har redigeringsrolle. */
export function erRedigerMøteMulig(møte: MøteModellEnkel, bruker: Brukerkontekst): boolean {
    return !møte.laastForRedigering && bruker.inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, møte.eiendeGruppeId);
}
